/**
 * Machine Service
 * API integration for machines (inventário NR 12) and their sub-resources.
 */
import apiClient from './apiClient';
import {
  HrnScale,
  Machine,
  MachineAdequacy,
  MachineAdequacyFormData,
  MachineFilters,
  MachineCreateData,
  MachineFormData,
  MachineHazardAssessment,
  MachineHazardAssessmentFormData,
  MachineListResponse,
  MachineProtection,
  MachineProtectionFormData,
} from '../types/machine';
import type { DependencyCheck } from '../types/dependencyCheck';

export * from '../types/machine';

export interface MachineListParams extends MachineFilters {
  page?: number;
  limit?: number;
}

type Id = string | number;

class MachineService {
  private baseUrl = '/machines';

  async getMachines(params: MachineListParams = {}): Promise<MachineListResponse> {
    const { page = 1, limit = 20, ...filters } = params;

    const queryParams = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.departmentId !== undefined) queryParams.append('departmentId', String(filters.departmentId));
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.hrnLevel) queryParams.append('hrnLevel', filters.hrnLevel);
    if (filters.hrnLevelMin) queryParams.append('hrnLevelMin', filters.hrnLevelMin);
    if (filters.semApreciacao) queryParams.append('semApreciacao', 'true');

    const response = await apiClient.get(`${this.baseUrl}?${queryParams.toString()}`);
    const backendData = response.data.data || response.data;

    return {
      machines: backendData.machines ?? [],
      total: backendData.total ?? 0,
      page: backendData.page || page,
      limit: backendData.limit || limit,
      totalPages: backendData.totalPages || Math.ceil((backendData.total ?? 0) / limit),
    };
  }

  async getMachineById(id: Id): Promise<Machine> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data.data || response.data;
  }

  async createMachine(data: MachineCreateData): Promise<Machine> {
    const response = await apiClient.post(this.baseUrl, data);
    return response.data.data || response.data;
  }

  async updateMachine(id: Id, data: Partial<MachineFormData>): Promise<Machine> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    return response.data.data || response.data;
  }

  async deleteMachine(id: Id): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  async checkDependencies(id: Id): Promise<DependencyCheck> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/dependencies`);
    return response.data.data || response.data;
  }

  async getHrnScale(): Promise<HrnScale> {
    const response = await apiClient.get(`${this.baseUrl}/hrn-scale`);
    return response.data.data || response.data;
  }

  async getProtections(machineId: Id): Promise<MachineProtection[]> {
    const response = await apiClient.get(`${this.baseUrl}/${machineId}/protections`);
    return response.data.data || response.data;
  }

  async createProtection(machineId: Id, data: MachineProtectionFormData): Promise<MachineProtection> {
    const response = await apiClient.post(`${this.baseUrl}/${machineId}/protections`, data);
    return response.data.data || response.data;
  }

  async updateProtection(
    machineId: Id,
    id: Id,
    data: Partial<MachineProtectionFormData>
  ): Promise<MachineProtection> {
    const response = await apiClient.put(`${this.baseUrl}/${machineId}/protections/${id}`, data);
    return response.data.data || response.data;
  }

  async deleteProtection(machineId: Id, id: Id): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${machineId}/protections/${id}`);
  }

  async getAdequacies(machineId: Id): Promise<MachineAdequacy[]> {
    const response = await apiClient.get(`${this.baseUrl}/${machineId}/adequacies`);
    return response.data.data || response.data;
  }

  async createAdequacy(machineId: Id, data: MachineAdequacyFormData): Promise<MachineAdequacy> {
    const response = await apiClient.post(`${this.baseUrl}/${machineId}/adequacies`, data);
    return response.data.data || response.data;
  }

  async updateAdequacy(
    machineId: Id,
    id: Id,
    data: Partial<MachineAdequacyFormData>
  ): Promise<MachineAdequacy> {
    const response = await apiClient.put(`${this.baseUrl}/${machineId}/adequacies/${id}`, data);
    return response.data.data || response.data;
  }

  async deleteAdequacy(machineId: Id, id: Id): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${machineId}/adequacies/${id}`);
  }

  async getHazardAssessments(machineId: Id): Promise<MachineHazardAssessment[]> {
    const response = await apiClient.get(`${this.baseUrl}/${machineId}/hazard-assessments`);
    return response.data.data || response.data;
  }

  async createHazardAssessment(
    machineId: Id,
    data: MachineHazardAssessmentFormData
  ): Promise<MachineHazardAssessment> {
    const response = await apiClient.post(`${this.baseUrl}/${machineId}/hazard-assessments`, data);
    return response.data.data || response.data;
  }

  async updateHazardAssessment(
    machineId: Id,
    id: Id,
    data: Partial<MachineHazardAssessmentFormData>
  ): Promise<MachineHazardAssessment> {
    const response = await apiClient.put(`${this.baseUrl}/${machineId}/hazard-assessments/${id}`, data);
    return response.data.data || response.data;
  }

  async deleteHazardAssessment(machineId: Id, id: Id): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${machineId}/hazard-assessments/${id}`);
  }
}

export const machineService = new MachineService();
export default machineService;
